import { challengeUrl, submissionUrl } from './form-site';

export interface TestSubmissionResult {
  ok: boolean;
  status: number;
  /** The server's explanation when the submission was rejected. */
  detail?: string;
}

// The widget's v3 payload: base64 of the challenge (without the code challenge) plus the solution.
async function solveCaptcha(formId: string): Promise<string> {
  const response = await fetch(`${challengeUrl(formId)}?track=false`);
  if (!response.ok) {
    throw new Error(`captcha challenge: HTTP ${response.status}`);
  }
  const challenge = await response.json();
  const { solveChallenge, pbkdf2, argon2id, sha } = await import('altcha/lib');
  const algorithm = String(challenge?.parameters?.algorithm ?? '').toUpperCase();
  const deriveKey = algorithm.startsWith('ARGON2')
    ? argon2id.deriveKey
    : algorithm.startsWith('PBKDF2')
      ? pbkdf2.deriveKey
      : sha.deriveKey;
  const solution = await solveChallenge({ challenge, deriveKey });
  if (!solution) {
    throw new Error('captcha challenge: no solution');
  }
  return btoa(
    JSON.stringify({
      challenge: { parameters: challenge.parameters, signature: challenge.signature },
      solution,
    })
  );
}

/** Posts a real, billed submission from this browser, solving the captcha the way the widget does. */
export async function sendTestSubmission(
  formId: string,
  data: Record<string, unknown>,
  captcha: boolean
): Promise<TestSubmissionResult> {
  const body: Record<string, unknown> = { ...data };
  if (captcha) {
    body.altcha = await solveCaptcha(formId);
  }
  const response = await fetch(`${submissionUrl(formId)}?redirect=none`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (response.ok) {
    return { ok: true, status: response.status };
  }
  let detail: string | undefined;
  try {
    const text = await response.text();
    try {
      const parsed = JSON.parse(text);
      detail =
        typeof parsed === 'string'
          ? parsed
          : (parsed?.message ?? parsed?.error ?? text) || undefined;
    } catch {
      detail = text || undefined;
    }
  } catch {
    detail = undefined;
  }
  return { ok: false, status: response.status, detail };
}
